<script setup lang="ts">
import AppLayout from '../../layouts/AppLayout.vue'
</script>

<template>
  <AppLayout>
    <div class="space-y-6">
      <!-- Header -->
      <div>
        <h1 class="text-3xl font-bold text-cc-text tracking-tight">Support Center</h1>
        <p class="text-cc-text-secondary mt-1">
          Get help and connect with our support team.
        </p>
      </div>

      <!-- Contact Card -->
      <div class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-100 dark:border-blue-800 rounded-xl p-6">
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
              <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-blue-900 dark:text-blue-100 text-lg">Need Help?</h3>
              <p class="text-blue-700 dark:text-blue-300 mt-1">
                Our support team is here to assist you with any questions or issues.
              </p>
            </div>
          </div>
          <a href="mailto:support@thechannelstandard.com" class="inline-flex items-center justify-center px-6 py-3 bg-cc-primary hover:bg-cc-primary-hover text-white font-medium rounded-lg transition shadow-sm">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Email Support
          </a>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- FAQ Section -->
        <div class="lg:col-span-2 space-y-6">
          <div class="bg-cc-elevated rounded-xl border border-cc-border p-6">
            <h2 class="text-lg font-bold text-cc-text mb-4">Frequently Asked Questions</h2>

            <div class="space-y-4">
              <div class="border-b border-cc-border pb-4">
                <h3 class="font-medium text-cc-text mb-2">How do I get started as an advisor?</h3>
                <p class="text-sm text-cc-text-secondary">
                  Once your account is set up, you can browse the supplier directory, view product details, and start using the AI assistant to match clients with the right solutions.
                </p>
              </div>

              <div class="border-b border-cc-border pb-4">
                <h3 class="font-medium text-cc-text mb-2">How do I update my supplier profile?</h3>
                <p class="text-sm text-cc-text-secondary">
                  Supplier administrators can update their company profile, products, and training materials from the supplier dashboard. Navigate to Settings to make changes.
                </p>
              </div>

              <div class="border-b border-cc-border pb-4">
                <h3 class="font-medium text-cc-text mb-2">What is the AI Companion feature?</h3>
                <p class="text-sm text-cc-text-secondary">
                  The AI Companion helps advisors find the best supplier matches for their clients by analyzing requirements and suggesting relevant solutions from the supplier portfolio.
                </p>
              </div>

              <div class="pb-2">
                <h3 class="font-medium text-cc-text mb-2">How do I access training materials?</h3>
                <p class="text-sm text-cc-text-secondary">
                  Training materials are available in the Training section. You can filter by supplier, topic, or content type to find relevant enablement resources.
                </p>
              </div>
            </div>

            <div class="mt-6 pt-4 border-t border-cc-border">
              <p class="text-sm text-cc-text-secondary">
                Don't see your question? Contact us at
                <a href="mailto:support@thechannelstandard.com" class="text-blue-600 dark:text-blue-400 hover:underline">
                  support@thechannelstandard.com
                </a>
              </p>
            </div>
          </div>
        </div>

        <!-- Right Column -->
        <div class="space-y-6">
          <!-- System Status Card -->
          <div class="bg-cc-elevated rounded-xl border border-cc-border p-6">
            <div class="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-4">
              <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 class="font-bold text-cc-text mb-2">System Status</h3>
            <p class="text-sm text-cc-text-secondary mb-4">
              Check the current status of Channel Standard services.
            </p>
            <div class="flex items-center gap-2">
              <span class="w-2 h-2 bg-green-500 rounded-full"></span>
              <span class="text-sm text-cc-success font-medium">All Systems Operational</span>
            </div>
          </div>

          <!-- Direct Contact Card -->
          <div class="bg-cc-elevated rounded-xl border border-cc-border p-6">
            <div class="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center mb-4">
              <svg class="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 class="font-bold text-cc-text mb-2">Contact Us</h3>
            <p class="text-sm text-cc-text-secondary mb-4">
              Reach out to our team for any questions or feedback.
            </p>
            <a href="mailto:support@thechannelstandard.com" class="text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium">
              support@thechannelstandard.com
            </a>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
